/**
 * AxiomAdmin.tsx — Admin → Marketplace → Axiom.
 * Thin wrapper over the reusable LabIntegrationAdmin.
 *
 * Two Axiom-family integrations share this page: pens ('axiom') and vials
 * ('axiom_vials'). When the vials module is enabled for the account, a
 * Pens / Vials tab bar switches between the two admin surfaces (one visible
 * at a time, pens first — same idiom as the patient Peptides tab). Pens-only
 * accounts see exactly the original single-surface page, no tab bar.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LabIntegrationAdmin } from '@/pages/admin/LabIntegrationAdmin';
import { getEnabledIntegrations } from '@/services/integrations.service';

export function AxiomAdmin() {
  const { data: integrations = [] } = useQuery({
    queryKey: ['integrations', 'enabled'],
    queryFn: getEnabledIntegrations,
    staleTime: 5 * 60_000,
  });
  const vialsEnabled = integrations.some((i) => i.key === 'axiom_vials');
  const [activeTab, setActiveTab] = useState<'pens' | 'vials'>('pens');

  const pens = (
    <LabIntegrationAdmin
      integrationKey="axiom"
      title={vialsEnabled ? 'Axiom Pens' : 'Axiom'}
      subtitle="Peptide / Rx pen orders shipped to the patient via Axiom Connect"
      vendorName="Axiom"
      requiresOnboarding
    />
  );

  if (!vialsEnabled) return pens;

  return (
    <div>
      <div className="flex items-center gap-1 border-b border-slate-200 px-6 pt-4">
        {([
          { key: 'pens', label: 'Pens' },
          { key: 'vials', label: 'Vials' },
        ] as const).map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors ${
              activeTab === tab.key
                ? 'border-b-2 border-blue-600 text-blue-700'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {activeTab === 'pens' ? (
        pens
      ) : (
        <LabIntegrationAdmin
          integrationKey="axiom_vials"
          title="Axiom Vials"
          subtitle="Peptide vials, GLP-1 vials, nasal sprays and supplies via Axiom Connect"
          vendorName="Axiom"
          requiresOnboarding
        />
      )}
    </div>
  );
}
